import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  downloadTimeClockExport,
  fetchTimeClockAdjustments,
  fetchTimeClockDayLocks,
  fetchTimeClockSettings,
  getHumanOperators,
  lockTimeClockDay,
  reviewTimeClockAdjustment,
  unlockTimeClockDay,
  updateTimeClockSettings,
} from "../../../api/client";
import {
  can,
  scopeSetFromPermissions,
  useEffectivePermissions,
} from "../../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../../context/AuthContext";
import UserTimeClockPanel from "../Users/UserTimeClockPanel";

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function markTypeLabel(type) {
  switch (type) {
    case "entry":
      return "Entrada";
    case "exit":
      return "Saída";
    case "break_start":
      return "Início intervalo";
    case "break_end":
      return "Fim intervalo";
    default:
      return type;
  }
}

function ConfigTab({ canEdit }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    employer_name: "",
    employer_cnpj: "",
    employer_address: "",
    rep_software_id: "XPRO4-REP-P",
    timezone: "America/Sao_Paulo",
    geo_required: false,
    tolerance_minutes: 10,
    min_break_minutes: 60,
    hours_bank_enabled: true,
    hours_bank_compensate_months: 6,
    overtime_destination: "hours_bank",
    deficit_destination: "hours_bank",
    overtime_weekday_percent: 50,
    overtime_weekend_percent: 100,
    max_daily_overtime_minutes: 120,
  });
  const [msg, setMsg] = useState("");

  const settingsQuery = useQuery(["time-clock-settings"], async () => {
    const res = await fetchTimeClockSettings();
    return res?.data?.data || res?.data || {};
  });

  useEffect(() => {
    const s = settingsQuery.data;
    if (!s) return;
    setForm({
      employer_name: s.employer_name || "",
      employer_cnpj: s.employer_cnpj || "",
      employer_address: s.employer_address || "",
      rep_software_id: s.rep_software_id || "XPRO4-REP-P",
      timezone: s.timezone || "America/Sao_Paulo",
      geo_required: Number(s.geo_required) === 1,
      tolerance_minutes: Number(s.tolerance_minutes) || 10,
      min_break_minutes: Number(s.min_break_minutes) || 60,
      hours_bank_enabled: s.hours_bank_enabled == null ? true : Number(s.hours_bank_enabled) === 1,
      hours_bank_compensate_months: Number(s.hours_bank_compensate_months) || 6,
      overtime_destination: s.overtime_destination || "hours_bank",
      deficit_destination: s.deficit_destination || "hours_bank",
      overtime_weekday_percent: Number(s.overtime_weekday_percent) || 50,
      overtime_weekend_percent: Number(s.overtime_weekend_percent) || 100,
      max_daily_overtime_minutes: Number(s.max_daily_overtime_minutes) || 120,
    });
  }, [settingsQuery.data]);

  const saveMutation = useMutation(() => updateTimeClockSettings(form), {
    onSuccess: () => {
      setMsg("Configuração salva.");
      qc.invalidateQueries(["time-clock-settings"]);
    },
    onError: (e) => setMsg(e?.response?.data?.message || e?.message || "Erro ao salvar"),
  });

  const setField = (key) => (e) => {
    const value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Dados do empregador e regras do REP-P (Portaria 671) para quem tem jornada legal
        (CLT, estágio ou Outro com horário). Prestador, função de confiança, Outro sem ponto
        e modalidade vazia não entram nestas regras.
      </Typography>
      {msg ? (
        <Alert
          severity={msg.includes("salva") ? "success" : "error"}
          sx={{ mb: 2 }}
          onClose={() => setMsg("")}
        >
          {msg}
        </Alert>
      ) : null}
      <Stack spacing={2}>
        <TextField label="Razão social / nome do empregador" value={form.employer_name} onChange={setField("employer_name")} fullWidth disabled={!canEdit} />
        <TextField label="CNPJ" value={form.employer_cnpj} onChange={setField("employer_cnpj")} fullWidth disabled={!canEdit} />
        <TextField label="Endereço" value={form.employer_address} onChange={setField("employer_address")} fullWidth disabled={!canEdit} />
        <TextField label="ID do coletor (REP-P software)" value={form.rep_software_id} onChange={setField("rep_software_id")} fullWidth disabled={!canEdit} />
        <TextField label="Timezone" value={form.timezone} onChange={setField("timezone")} fullWidth disabled={!canEdit} />
        <TextField label="Tolerância (minutos)" type="number" value={form.tolerance_minutes} onChange={setField("tolerance_minutes")} fullWidth disabled={!canEdit} />
        <TextField label="Intervalo mínimo (minutos)" type="number" value={form.min_break_minutes} onChange={setField("min_break_minutes")} fullWidth disabled={!canEdit} />
        <FormControlLabel
          control={<Switch checked={form.geo_required} onChange={setField("geo_required")} disabled={!canEdit} />}
          label="Exigir geolocalização nas marcações"
        />
        <Typography variant="subtitle2" fontWeight={700} sx={{ pt: 1 }}>
          CLT — banco de horas, extra e desconto
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Vale só para CLT com controle de ponto. Atraso e permanência após o expediente no
          escritório virtual usam estas regras (arts. 58, 59 e 59-A). Estágio e prestador não entram.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={form.hours_bank_enabled}
              onChange={setField("hours_bank_enabled")}
              disabled={!canEdit}
            />
          }
          label="Usar banco de horas (compensação em vez de pagar toda extra)"
        />
        <TextField
          label="Prazo para compensar o banco (meses)"
          type="number"
          value={form.hours_bank_compensate_months}
          onChange={setField("hours_bank_compensate_months")}
          helperText="Acordo individual: até 6 meses. Acordo coletivo: até 12 meses."
          fullWidth
          disabled={!canEdit}
        />
        <FormControl fullWidth disabled={!canEdit}>
          <InputLabel>Destino das horas extras</InputLabel>
          <Select
            label="Destino das horas extras"
            value={form.overtime_destination}
            onChange={setField("overtime_destination")}
          >
            <MenuItem value="hours_bank">Banco de horas</MenuItem>
            <MenuItem value="paid">Pagar como hora extra (adicional)</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={!canEdit}>
          <InputLabel>Destino de atraso e déficit</InputLabel>
          <Select
            label="Destino de atraso e déficit"
            value={form.deficit_destination}
            onChange={setField("deficit_destination")}
          >
            <MenuItem value="hours_bank">Débito no banco de horas</MenuItem>
            <MenuItem value="payroll_discount">Desconto em folha</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Adicional de extra em dia útil (%)"
          type="number"
          value={form.overtime_weekday_percent}
          onChange={setField("overtime_weekday_percent")}
          helperText="Mínimo legal 50% (CLT art. 59)."
          fullWidth
          disabled={!canEdit}
        />
        <TextField
          label="Adicional de extra em domingo/feriado (%)"
          type="number"
          value={form.overtime_weekend_percent}
          onChange={setField("overtime_weekend_percent")}
          fullWidth
          disabled={!canEdit}
        />
        <TextField
          label="Teto diário de hora extra (minutos)"
          type="number"
          value={form.max_daily_overtime_minutes}
          onChange={setField("max_daily_overtime_minutes")}
          helperText="Padrão 120 minutos (2h — CLT art. 59)."
          fullWidth
          disabled={!canEdit}
        />
        {canEdit ? (
          <Button variant="contained" disabled={saveMutation.isLoading} onClick={() => saveMutation.mutate()} sx={{ alignSelf: "flex-start" }}>
            Salvar
          </Button>
        ) : (
          <Alert severity="info">Sem permissão para alterar a configuração (precisa de adjust ou export).</Alert>
        )}
      </Stack>
    </Box>
  );
}

function AdjustmentsTab({ canReview }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const [msg, setMsg] = useState("");
  const [notesById, setNotesById] = useState({});

  const listQuery = useQuery(
    ["time-clock-adjustments", status],
    async () => {
      const res = await fetchTimeClockAdjustments({ status: status === "all" ? undefined : status });
      return res?.data?.data || res?.data || [];
    },
    { refetchInterval: 30000 }
  );

  const review = useMutation(
    ({ id, approve, reviewNotes }) => reviewTimeClockAdjustment(id, { approve, reviewNotes }),
    {
      onSuccess: (_, vars) => {
        setMsg(vars.approve ? "Justificativa aprovada." : "Justificativa rejeitada.");
        qc.invalidateQueries(["time-clock-adjustments"]);
        qc.invalidateQueries(["time-clock-mirror"]);
        qc.invalidateQueries(["time-clock-marks"]);
      },
      onError: (e) => setMsg(e?.response?.data?.message || e?.message || "Erro ao revisar"),
    }
  );

  const rows = listQuery.data || [];

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="pending">Pendentes</MenuItem>
            <MenuItem value="approved">Aprovadas</MenuItem>
            <MenuItem value="rejected">Rejeitadas</MenuItem>
            <MenuItem value="all">Todas</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {rows.length} pedido(s)
        </Typography>
      </Stack>
      {msg ? (
        <Alert severity={msg.toLowerCase().includes("erro") ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMsg("")}>
          {msg}
        </Alert>
      ) : null}
      {!canReview ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Você pode visualizar; aprovação exige permissão time_clock.adjust.
        </Alert>
      ) : null}
      <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Colaborador</TableCell>
              <TableCell>Solicitado</TableCell>
              <TableCell>Justificativa</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {row.user_name || `#${row.user_id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    por {row.requested_by_name || row.requested_by} · {String(row.created_at || "").slice(0, 16)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {markTypeLabel(row.requested_mark_type)}
                  <br />
                  <Typography variant="caption">{String(row.requested_marked_at).slice(0, 16)}</Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>{row.justificativa}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status}
                    color={row.status === "pending" ? "warning" : row.status === "approved" ? "success" : "default"}
                  />
                </TableCell>
                <TableCell>
                  {row.status === "pending" && canReview ? (
                    <Stack spacing={1}>
                      <TextField
                        size="small"
                        placeholder="Nota da revisão"
                        value={notesById[row.id] || ""}
                        onChange={(e) =>
                          setNotesById((p) => ({ ...p, [row.id]: e.target.value }))
                        }
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={review.isLoading}
                          onClick={() =>
                            review.mutate({
                              id: row.id,
                              approve: true,
                              reviewNotes: notesById[row.id] || null,
                            })
                          }
                        >
                          Aprovar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={review.isLoading}
                          onClick={() =>
                            review.mutate({
                              id: row.id,
                              approve: false,
                              reviewNotes: notesById[row.id] || null,
                            })
                          }
                        >
                          Rejeitar
                        </Button>
                      </Stack>
                    </Stack>
                  ) : row.review_notes ? (
                    <Typography variant="caption">{row.review_notes}</Typography>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum pedido neste filtro.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function UserPicker({ value, onChange, operators }) {
  return (
    <FormControl size="small" sx={{ minWidth: 260 }}>
      <InputLabel>Colaborador</InputLabel>
      <Select
        label="Colaborador"
        value={value || ""}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      >
        {(operators || []).map((u) => (
          <MenuItem key={u.id} value={u.id}>
            {u.nome || u.name || `#${u.id}`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function MirrorTab({ operators, canExport }) {
  const [userId, setUserId] = useState(null);
  const selected = (operators || []).find((u) => Number(u.id) === Number(userId));
  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <UserPicker value={userId} onChange={setUserId} operators={operators} />
      </Stack>
      {userId ? (
        <UserTimeClockPanel
          userId={userId}
          userName={selected?.nome || selected?.name}
          showMatricula
          showManualMark
          showAdjustment
        />
      ) : (
        <Alert severity="info">
          Selecione um colaborador para ver espelho, exportações{canExport ? ", AFD/AEJ/PDF" : ""} e
          justificativas.
        </Alert>
      )}
    </Box>
  );
}

function ExportTab({ operators, canExport }) {
  const [userId, setUserId] = useState(null);
  const [start, setStart] = useState(() => dayjs().startOf("month"));
  const [end, setEnd] = useState(() => dayjs());
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const startStr = start?.format?.("YYYY-MM-DD");
  const endStr = end?.format?.("YYYY-MM-DD");

  if (!canExport) {
    return <Alert severity="warning">Sem permissão time_clock.export para baixar arquivos.</Alert>;
  }

  const run = async (kind) => {
    setBusy(kind);
    setMsg("");
    try {
      await downloadTimeClockExport(kind, {
        userId: kind === "afd-bulk" ? undefined : userId,
        start: startStr,
        end: endStr,
      });
      setMsg(`Arquivo ${kind} baixado.`);
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.message || "Falha na exportação");
    } finally {
      setBusy("");
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Exportações para contabilidade (uso interno). AFD/AEJ são layouts simplificados Portaria
        671 — sem homologação MTP. Só entram contratos com jornada (CLT, estágio, Outro com horário).
        Prestador, confiança e Outro sem ponto ficam de fora.
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <UserPicker value={userId} onChange={setUserId} operators={operators} />
          <DatePicker label="Início" value={start} onChange={setStart} slotProps={{ textField: { size: "small" } }} />
          <DatePicker label="Fim" value={end} onChange={setEnd} slotProps={{ textField: { size: "small" } }} />
        </Stack>
      </LocalizationProvider>
      {msg ? (
        <Alert severity={msg.toLowerCase().includes("falha") ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMsg("")}>
          {msg}
        </Alert>
      ) : null}
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button variant="outlined" disabled={!userId || Boolean(busy)} onClick={() => run("payroll")}>
          Espelho TXT
        </Button>
        <Button variant="outlined" disabled={!userId || Boolean(busy)} onClick={() => run("payroll-pdf")}>
          Espelho PDF
        </Button>
        <Button variant="outlined" disabled={!userId || Boolean(busy)} onClick={() => run("afd")}>
          AFD (1 colaborador)
        </Button>
        <Button variant="outlined" disabled={!userId || Boolean(busy)} onClick={() => run("aej")}>
          AEJ
        </Button>
        <Button variant="contained" disabled={Boolean(busy)} onClick={() => run("afd-bulk")}>
          AFD em lote (período)
        </Button>
      </Stack>
    </Box>
  );
}

function LocksTab({ operators, canAdjust }) {
  const qc = useQueryClient();
  const [userId, setUserId] = useState(null);
  const [workDate, setWorkDate] = useState(() => dayjs());
  const [notes, setNotes] = useState("");
  const [msg, setMsg] = useState("");
  const start = dayjs().startOf("month").format("YYYY-MM-DD");
  const end = dayjs().endOf("month").format("YYYY-MM-DD");

  const locksQuery = useQuery(
    ["time-clock-day-locks", userId, start, end],
    async () => {
      const res = await fetchTimeClockDayLocks({
        userId: userId || undefined,
        start,
        end,
      });
      return res?.data?.data || res?.data || [];
    },
    { enabled: canAdjust || true }
  );

  const lockMut = useMutation(
    () =>
      lockTimeClockDay({
        userId,
        workDate: workDate.format("YYYY-MM-DD"),
        notes: notes || null,
      }),
    {
      onSuccess: () => {
        setMsg("Dia fechado.");
        qc.invalidateQueries(["time-clock-day-locks"]);
      },
      onError: (e) => setMsg(e?.response?.data?.message || e?.message || "Erro"),
    }
  );

  const unlockMut = useMutation(
    ({ uid, date }) => unlockTimeClockDay({ userId: uid, workDate: date }),
    {
      onSuccess: () => {
        setMsg("Dia desbloqueado.");
        qc.invalidateQueries(["time-clock-day-locks"]);
      },
      onError: (e) => setMsg(e?.response?.data?.message || e?.message || "Erro"),
    }
  );

  const rows = locksQuery.data || [];

  return (
    <Box>
      {!canAdjust ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Fechar/desbloquear dias exige time_clock.adjust.
        </Alert>
      ) : null}
      {msg ? (
        <Alert severity={msg.toLowerCase().includes("erro") ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMsg("")}>
          {msg}
        </Alert>
      ) : null}
      {canAdjust ? (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems="center">
            <UserPicker value={userId} onChange={setUserId} operators={operators} />
            <DatePicker label="Dia" value={workDate} onChange={setWorkDate} slotProps={{ textField: { size: "small" } }} />
            <TextField size="small" label="Nota" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <Button
              variant="contained"
              disabled={!userId || lockMut.isLoading}
              onClick={() => lockMut.mutate()}
            >
              Fechar dia
            </Button>
          </Stack>
        </LocalizationProvider>
      ) : null}
      <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Colaborador</TableCell>
              <TableCell>Dia</TableCell>
              <TableCell>Fechado em</TableCell>
              <TableCell>Por</TableCell>
              <TableCell>Nota</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.user_name || `#${r.user_id}`}</TableCell>
                <TableCell>{String(r.work_date).slice(0, 10)}</TableCell>
                <TableCell>{String(r.locked_at || "").slice(0, 16)}</TableCell>
                <TableCell>{r.locked_by_name || r.locked_by || "—"}</TableCell>
                <TableCell>{r.notes || "—"}</TableCell>
                <TableCell>
                  {canAdjust ? (
                    <Button
                      size="small"
                      onClick={() =>
                        unlockMut.mutate({
                          uid: r.user_id,
                          date: String(r.work_date).slice(0, 10),
                        })
                      }
                    >
                      Desbloquear
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhum dia fechado neste mês.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function TimeClockSettingsPage() {
  const { userData } = useAuthContext();
  const isMasterUser = Number(userData?.master) === 1;
  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(
    () =>
      scopeSetFromPermissions(permData, {
        ...userData,
        master: isMasterUser ? 1 : 0,
      }),
    [permData, userData, isMasterUser]
  );
  const canAdjust = can(scopeSet, "time_clock", "adjust");
  const canExport = can(scopeSet, "time_clock", "export");
  const canEditConfig = canAdjust || canExport;

  const [tab, setTab] = useState(0);

  const operatorsQuery = useQuery(["time-clock-operators"], async () => {
    const res = await getHumanOperators(500);
    return res?.operators || [];
  });

  const operators = operatorsQuery.data || [];

  return (
    <Box sx={{ p: 2, maxWidth: 1100 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        Controle de Ponto
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Hub operacional REP-P para quem tem jornada legal (CLT, estágio ou Outro com horário).
        Prestador, função de confiança, Outro sem ponto e modalidade vazia não entram em AFD/espelho.
        Configuração, justificativas, espelho, exportações e fechamento de período.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Configuração" />
        <Tab label="Justificativas" />
        <Tab label="Espelho" />
        <Tab label="Exportações" />
        <Tab label="Fechamento" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <ConfigTab canEdit={canEditConfig} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <AdjustmentsTab canReview={canAdjust} />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <MirrorTab operators={operators} canExport={canExport} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <ExportTab operators={operators} canExport={canExport} />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <LocksTab operators={operators} canAdjust={canAdjust} />
      </TabPanel>
    </Box>
  );
}
